import { memo, useMemo, useRef, useState } from 'react'
import { css, jsx } from '@emotion/react'
import { useDebounce } from 'use-debounce'
import { useRecoilState } from 'recoil'
import FavBookmarkListContainer from './FavBookmarkListContainer'
import FavSearchHeader from './FavSearchHeader'
import useRect from '../../../utils/hooks/useRect'
import { favBookmarksSearchMode, favBookmarksSortMode, favBookmarksViewMode } from '../../../store/fav'
import MaxWidth from '../../MaxWidth'

const styles = {
  listWrapper: (padding, topHeaderHeight) => css`
    padding: ${padding} !important;
    height: calc(100% - ${topHeaderHeight}px);
    overflow-y: auto;
    overflow-x: hidden;
  `,
}

const menuSearchOptions = [
  { label: 'Title', value: 'title' },
  { label: 'Description', value: 'description' },
  { label: 'URL', value: 'url' },
  { label: 'Tags', value: 'tags' },
  { label: 'Full-body text', value: 'text', premium: true },
]

const sortModeOptions = [
  { label: 'Creation date Asc  ↑', value: 'createdAt' },
  { label: 'Creation date Des  ↓', value: '-createdAt', default: true },
  { label: 'Bookmark title Asc (A-Z)', value: 'title' },
  { label: 'Bookmark title Des (Z-A)', value: '-title' },
]

const viewModeOptions = [
  { label: 'Type card', value: 'viewLikeCard' },
  { label: 'Show tags', value: 'showTags' },
  { label: 'Show URL', value: 'showUrl' },
  { label: 'Show description', value: 'showDescription' },
  { label: 'Show date', value: 'showDate', premium: true },
]

function FavDataWrapper({ padding, bookmarks, isErrorBookmarks }) {
  const [inputValue, setInputValue] = useState('')
  const [scrollPassLimit, setScrollPassLimit] = useState(false)
  const [largeDebounceTime, setLargeDebounceTime] = useState(false)
  const debounceTime = largeDebounceTime ? 350 : 0
  const [value] = useDebounce(inputValue, debounceTime)
  const [bookmarksViewMode, setBookmarksViewMode] = useRecoilState(favBookmarksViewMode)
  const [bookmarksSortMode, setBookmarksSortMode] = useRecoilState(favBookmarksSortMode)
  const [bookmarksSearchMode, setBookmarksSearchMode] = useRecoilState(favBookmarksSearchMode)

  const headerRef = useRef()
  const { height: topHeaderHeight } = useRect(headerRef)

  const or = useMemo(() => {
    if (!value) return
    return {
      $or: menuSearchOptions
        .filter(option => bookmarksSearchMode[option.value])
        .map(option => ({
          [option.value]: { $regex: value, $options: 'i' },
        })),
    }
  }, [bookmarksSearchMode, value, menuSearchOptions])

  const options = useMemo(() => {
    const orOpt = or ?? {}

    return {
      params: {
        options: {
          favourite: true,
          ...orOpt,
        },
        sort: bookmarksSortMode,
        select: 'title image description url favourite folder updatedAt createdAt color',
      },
    }
  }, [or, bookmarksSortMode])

  return (
    <>
      <FavSearchHeader
        ref={headerRef}
        padding={padding}
        setInputValue={setInputValue}
        inputValue={inputValue}
        menuSearchOptions={menuSearchOptions}
        viewMode={bookmarksViewMode}
        setViewMode={setBookmarksViewMode}
        sortMode={bookmarksSortMode}
        setSortMode={setBookmarksSortMode}
        searchMode={bookmarksSearchMode}
        setSearchMode={setBookmarksSearchMode}
        sortModeOptions={sortModeOptions}
        scrollPassLimit={scrollPassLimit}
        viewModeOptions={viewModeOptions}
        setLargeDebounceTime={setLargeDebounceTime}
        count={bookmarks ? bookmarks.length : undefined}
      />
      <MaxWidth
        css={styles.listWrapper(padding, topHeaderHeight)}
        className="custom-scrollbar-guardo"
        onScroll={e => {
          const { scrollTop } = e.target
          if (scrollTop > 10) {
            if (!scrollPassLimit) return setScrollPassLimit(true)
            return
          }
          if (scrollPassLimit) setScrollPassLimit(false)
        }}
      >
        <FavBookmarkListContainer
          options={options}
          padding={padding}
          isSearching={!!inputValue}
          bookmarks={bookmarks}
          bookmarksViewMode={bookmarksViewMode}
          isErrorBookmarks={isErrorBookmarks}
        />
      </MaxWidth>
    </>
  )
}

export default memo(FavDataWrapper)
